import React, { useCallback, useEffect, useState } from 'react';
import { Logger } from '@saviynt/common';
import { Box, Icon, Loader } from '@saviynt/design-system';
import { useHistory, useParams } from 'react-router-dom';

import type { RuleAction, RuleCondition } from '../../services';

import { useRuleRecommendationDetails } from '../../hooks/use-rulerecommendations';
import RulesService, {
  type ActivateRuleResponse,
} from '../../services/RulesService/RulesService';
import { normalizeImpactLevel } from '../../shared/utils';
import { getUserKey, getUserName } from '../../utils';
import {
  getErrorMessage,
  isRuleAlreadyExistsError,
} from '../../utils/errorHandlingHttp';

import AppAccessTiles from './AppAccessTiles/AppAccessTiles';
import FooterActionBar from './FooterActionBar/FooterActionBar';
import RuleConditions from './RuleConditions/RuleConditions';
import RuleDetailsHeader from './RuleDetailsHeader/RuleDetailsHeader';
// MVP2: import RuleInfo from './RuleInfo/RuleInfo';
import {
  type ActivateRuleFormData,
  ActivateRuleModal,
} from './ActivateRuleModal';
import { IncludedEntitlements } from './IncludedEntitlements';

import './RuleDetails.css';

function RuleDetails() {
  // Get the rule ID from the URL params
  const { id } = useParams<{ id: string }>();

  const history = useHistory();

  useEffect(() => {
    // auto scroll to top: router uses scroll position from dashboard
    window.scrollTo(0, 0);
  }, []);

  // Modal state
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);

  // Track current rule actions (excludes deleted entitlement groups)
  // This is the single source of truth for current rule state used by:
  // - AppAccessTiles: Shows apps with correct entitlement counts
  // - ActivateRuleModal: Sends correct actions to activate API
  // - Future components: Any component needing current rule data
  const [ruleActions, setRuleActions] = useState<RuleAction[]>([]);

  // Handle rule data changes from IncludedEntitlements component
  // Called when user deletes entitlement groups or other rule modifications occur
  const handleRuleDataChange = useCallback((actions: RuleAction[]) => {
    setRuleActions(actions);
  }, []);

  const ruleId = parseInt(id, 10);
  const { data, isLoading, error } = useRuleRecommendationDetails(ruleId);

  // check if activate button is disabled based on user access
  const isActivateButtonDisabled = data?.userAccess !== 'edit';

  if (error) {
    return (
      <div>Error: {error instanceof Error ? error.message : String(error)}</div>
    );
  }

  if (isLoading) {
    return (
      <div className='Rules-RuleDetails-loader'>
        <Loader kind='loop' format='main' color='brand' />
      </div>
    );
  }

  if (!data) {
    // TODO: show the proper error message.
    return <div>No data found</div>;
  }

  // Check for an empty actions array or any null evKey and redirect to prevent crashes
  if (data.result.data[0]) {
    const ruleData = data.result.data[0];

    if (
      ruleData.actions.length === 0 ||
      ruleData.actions.some(
        (action: RuleAction) => !action.entitlementMetaData.evKey
      )
    ) {
      history.push('../');

      return <div>Redirecting...</div>;
    }
  }

  const getAppsFromActions = (
    // TODO: remove when we get the count from the API when ready.
    actions: RuleAction[] = []
  ) => {
    const appMap = new Map<
      string,
      { name: string; numEntitlements: number; applicationIcon: string }
    >();

    actions.forEach((action: RuleAction) => {
      const { endpointName, logo } = action.entitlementMetaData.endpoint;
      const existing = appMap.get(endpointName);

      if (existing) {
        existing.numEntitlements += 1;
      } else {
        appMap.set(endpointName, {
          name: endpointName,
          numEntitlements: 1,
          applicationIcon: logo,
        });
      }
    });

    return Array.from(appMap.values());
  };

  // Handle opening the activate modal
  const handleActivateRule = () => {
    setIsActivateModalOpen(true);
  };

  // Handle closing the activate modal
  const handleCloseModal = () => {
    setIsActivateModalOpen(false);
    setIsSaving(false);
    setIsActivating(false);
    setActivationError(null);
  };

  const handleClearActivationError = () => {
    setActivationError(null);
  };

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const handleActivateSuccess = (
    response: ActivateRuleResponse,
    formData: ActivateRuleFormData
  ) => {
    try {
      // Store success data for banner display on dashboard
      sessionStorage.setItem(
        'ruleActivationSuccess',
        JSON.stringify({
          ruleName: formData.ruleName,
          ruleId: response.ruleId,
          timestamp: Date.now(), // For cleanup safety
        })
      );
    } catch (storageError) {
      Logger.warn(
        'SessionStorage failed, redirecting without banner',
        storageError
      );
    }

    window.location.href = '../';
  };

  const handleActivateError = (
    apiError: Error,
    formData: ActivateRuleFormData
  ) => {
    // Check if it's a duplicate rule name error
    if (isRuleAlreadyExistsError(apiError)) {
      setActivationError(
        'A technical rule already exists in the system with this name'
      );
      setIsActivating(false);

      return;
    }

    // For all other errors, redirect to dashboard with error banner
    try {
      // Store error data for banner display on dashboard
      sessionStorage.setItem(
        'ruleActivationError',
        JSON.stringify({
          ruleName: formData.ruleName,
          errorMessage: getErrorMessage(apiError),
          timestamp: Date.now(),
        })
      );
    } catch (storageError) {
      Logger.warn(
        'SessionStorage failed, redirecting without banner',
        storageError
      );
    }

    window.location.href = '../';
  };

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const handleSave = (formData: ActivateRuleFormData) => {
    // TODO: save btn is for future mvp - implement api call
    setIsSaving(true);

    // await saveRule(ruleId, formData);

    // Simulate API call
    setTimeout(() => {
      Logger.info('Rule saved successfully');
      handleCloseModal();
    }, 1000);
  };

  // Handle save and activate action
  const handleSaveAndActivate = async (
    formData: ActivateRuleFormData,
    actions: RuleAction[]
  ) => {
    setIsActivating(true);

    try {
      const userKey = getUserKey();
      const userName = getUserName();

      if (!userKey) {
        throw new Error('User key not found');
      }

      if (!userName) {
        throw new Error('Username not found');
      }

      // Transform actions to the format expected by the API
      const apiActions = actions.map((action) => ({
        entitlementValueName: action.entitlementMetaData.entitlementValueName,
        entitlementTypeName:
          action.entitlementMetaData.entitlementType.entitlementTypeName,
        endpointName: action.entitlementMetaData.endpoint.endpointName,
      }));

      const requestBody = {
        activatedBy: parseInt(userKey, 10),
        name: formData.ruleName,
        description: formData.description,
        owner: userName, // TODO: confirm this is the logged in username
        actions: apiActions,
        birthRightAccessEnabled: formData.isBirthrightAccess,
        removeBirthRightAccessOnConditionFailure:
          formData.isRemoveAccessWhenNotSatisfied,
      };

      const response = await RulesService.activateRule(
        ruleId.toString(),
        requestBody
      );

      handleActivateSuccess(response, formData);
    } catch (apiError) {
      Logger.error('Failed to activate rule:', apiError);

      handleActivateError(apiError as Error, formData);
    }
  };

  return (
    <Box className='Rules-RuleDetails'>
      <RuleDetailsHeader
        // TODO: localize strings in mvp2
        title={`${data.result.data[0]?.includedUsers ?? 0} Included Users`}
        subtitle='Review rule conditions and included access before converting to a Technical or Birthright Rule'
        showBreadcrumbs
        impactLevel={normalizeImpactLevel(data.result.data[0]?.impact)}
        icon={<Icon kind='Security' size='large' />}
      />

      {/* 
      // TODO: enhance/show this component in mvp2.
      <RuleInfo name={data?.name} description={data?.description} /> */}

      {/* each rule condition is a row separated by 'AND' at the right. */}
      <RuleConditions
        conditions={
          data.result.data[0]?.conditions.map((condition: RuleCondition) => ({
            name: condition.objectName,
            operator: condition.operation,
            values: Array.isArray(condition.objectValue)
              ? condition.objectValue
              : [condition.objectValue],
          })) ?? []
        }
      />

      <AppAccessTiles
        // Uses current rule actions (excludes deleted entitlement groups)
        // This ensures app tiles show only apps that still exist in the rule
        title='Access will be provided to these apps'
        apps={getAppsFromActions(
          ruleActions.length > 0 ? ruleActions : data.result.data[0]?.actions
        )}
      />

      <IncludedEntitlements
        actions={data.result.data[0]?.actions ?? []}
        ruleData={
          data.result.data[0]
            ? {
                userMatchCount: data.result.data[0].userMatchCount,
                confidence: data.result.data[0].confidence,
                entitlements: data.result.data[0].entitlements,
                conditions: data.result.data[0].conditions,
              }
            : undefined
        }
        onRuleDataChange={handleRuleDataChange} // Callback to receive current rule state
      />

      <FooterActionBar
        onBack={() => {
          history.replace('../');
        }}
        onActivateRule={handleActivateRule}
        isActivating={isActivating}
        isActivateButtonDisabled={isActivateButtonDisabled}
      />

      {/* Activate Rule Modal */}
      <ActivateRuleModal
        isOpen={isActivateModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        onSaveAndActivate={(formData, actions) => {
          handleSaveAndActivate(formData, actions).catch((err) => {
            Logger.error('Unhandled activation error', err);
          });
        }}
        isSaving={isSaving}
        isActivating={isActivating}
        actions={ruleActions} // Uses current rule actions (excludes deleted entitlement groups)
        errorMessage={activationError ?? undefined}
        onClearError={handleClearActivationError}
      />
    </Box>
  );
}

export default RuleDetails;
